/**
 * The desktop fallback, in the design canvas's own numbers.
 *
 * Every value below is the literal pixel the canvas source sets. Nothing here is measured
 * off a screenshot and nothing is to taste — if the canvas says 22, this says 22.
 *
 * The one thing that cannot be copied verbatim is the unit. The canvas lays the artboard
 * out at `width: 100%` inside a document column and then sizes everything in fixed pixels,
 * which only describes a screen at the width the artboard was drawn at; shipped full-bleed,
 * those same pixels would render a 22px headline on a 4K display. So the pixels are divided
 * by the artboard width and spent as fractions of the stage, which reproduces the canvas
 * exactly at that width and holds its proportions everywhere else.
 *
 * ARTBOARD_WIDTH is the one derived number in the file, and the type corroborates it: the
 * canvas's 22px headline is 2.52% of the stage in the review's reference render, and
 * 22 / 0.0252 is 873. Every other value falls out of that same division.
 *
 * Consumers read these through the `--stage-w` custom property. Kept in their own module
 * rather than inside the component so they can be asserted directly.
 */

import { withAlpha } from './fallbackColor';

/** The width the canvas artboard's pixel values describe. */
export const ARTBOARD_WIDTH = 873;

/**
 * Where the story side ends and the scan panel begins.
 * The canvas splits the stage `1.25fr 0.75fr`, which is this to the digit.
 */
export const MEDIA_PANEL_WIDTH = '62.5%';

/** The canvas frame's own zoom, covering the soft edge the blur pulls in. */
export const DESKTOP_MEDIA_ZOOM = 1.08;

/** Literal canvas pixels. Spend them through `desktopPx`, never directly. */
export const DESKTOP_PX = {
  headline: 22,
  subcopy: 14,
  qrCaption: 14,

  /** The code itself, then the card's padding around it, then the rim's. */
  qr: 148,
  qrCardPadding: 16,
  qrRimWidth: 5,
  qrRimRadius: 16,
  qrCardRadius: 13,
  qrCaptionGap: 12,
  scanPanelPaddingY: 20,
  scanPanelPaddingX: 24,

  /** The story side's gutter, and the inset above the brand stack. */
  gutter: 28,
  logoStackTop: 18,
  logoGap: 10,

  copyTop: 16,
  // Traded into `poweredByOffset` so the mark sits lower without lifting the copy above it —
  // the block is foot-pinned, so growing the gap under the subcopy without shrinking this
  // would push the headline up.
  copyBottom: 14,
  copyGap: 8,
  copyMaxWidth: 420,

  // Drawn at 80% of the slot it occupies. The slot stays at the original 28 so the copy
  // above — pinned to the foot of the panel via justify-end — does not slide down when the
  // mark shrinks. Aligned to the foot of the slot so the spare height lands above the mark
  // and joins the offset in dropping it.
  poweredBy: 22.4,
  poweredBySlot: 28,
  poweredByGap: 10,
  poweredByOffset: 24,

  scrimBlur: 3,
} as const;

/** A share of the stage width, for the values the dashboard states as fractions. */
export const desktopScaled = (fraction: number) => `calc(var(--stage-w) * ${fraction})`;

/** A canvas pixel, as a share of the stage. */
export const desktopPx = (canvasPx: number) => desktopScaled(canvasPx / ARTBOARD_WIDTH);

/** The card behind the code: the QR plus the padding the canvas puts around it. */
export const QR_CARD_PX = DESKTOP_PX.qr + DESKTOP_PX.qrCardPadding * 2;

/**
 * The scan panel, as glass over the same photograph.
 *
 * A second, harder-blurred copy of the media tinted to this alpha — not an opaque fill,
 * which would cut the stage in two, and not a bare tint, which would leave the game
 * legible straight through the QR.
 *
 * Solved from the canvas rather than chosen: sampling its panel against the media beside it
 * puts the mix at roughly this much tint. Pushing past it — which an earlier round did, on
 * the theory that a bright campaign needed holding down — darkens the panel and pulls it
 * toward the stage colour's magenta, which is the opposite of the desaturated blue-violet
 * the canvas actually renders, and is what review picked up as the wrong purple.
 */
export const PANEL_TINT_ALPHA = 0.78;

/**
 * The seam down the left of the scan panel: a hairline of the accent, which is what stops
 * the glass edge reading as a rendering artefact rather than an intended join.
 */
export const PANEL_EDGE_ALPHA = 0.25;

/**
 * The wedge under the copy.
 *
 * Heavy against the left edge and effectively gone by the time it reaches the scan panel,
 * so the campaign keeps its picture while the headline still has something to sit on. The
 * ramp runs left to right; how far up it reaches is the mask's job, which keeps the two
 * axes independent.
 *
 * Its height is not set here on purpose. The canvas pins the scrim to the copy block it
 * serves, so it is exactly as tall as that block — a fixed height would have to be
 * re-guessed every time the copy wrapped to another line.
 */
export const SCRIM_STOPS: ReadonlyArray<readonly [percent: number, alpha: number]> = [
  [0, 0.88],
  [36, 0.58],
  [68, 0.28],
  [100, 0.06],
];

/**
 * How much of the scrim's height goes on arriving.
 *
 * Long enough that the ramp is still climbing when it reaches the headline and settles on
 * the letters themselves. Anything shorter tops out above them and holds, which paints a
 * band of flat purple with nothing in it between the fade's edge and the type — the gap
 * review kept returning to. Measuring the canvas puts that band at a quarter of a cap
 * height; ours was six times that, from a ramp that finished early and then coasted.
 *
 * Set against `copyTop`, which is the only thing between the scrim's top edge and the
 * headline now that the scrim covers the copy block exactly: the ramp has to outlast that
 * padding and give out before the caps.
 */
export const SCRIM_FADE_IN = '13%';

/**
 * How far across the story side the ramp runs.
 *
 * Full width, so it ends on the seam the scan panel starts at rather than short of it.
 */
export const SCRIM_WIDTH = '100%';

/**
 * How far the scrim is pushed past the stage's left and bottom edges.
 *
 * `filter: blur()` softens the element's own alpha, not just the picture behind it, so a
 * scrim whose box stops on the stage boundary spends its last pixels of coverage fading
 * into nothing right at the edge.
 *
 * The blur's visible reach is roughly 3σ and σ is half the radius, so anything past about
 * 1.5× `scrimBlur` clears it; this leaves room. The stage clips its overflow, so the bleed
 * is free — it only moves the falloff off-screen.
 */
export const SCRIM_BLEED_PX = 12;

/**
 * How far the scrim starts above the copy block, as a share of that block's height.
 *
 * The scrim used to begin exactly on the block's top edge, which gave the ramp only
 * `copyTop` to arrive in and started the wash abruptly. This lifts its top edge without
 * touching where the ramp lands: the extra height is spent entirely on a longer approach,
 * so there is more fade above the headline and the same amount of nothing over it.
 *
 * Proportional rather than a canvas pixel because it is measured against the block it
 * overhangs, which is itself sized by however the copy wraps.
 */
export const SCRIM_OVERHANG = '24%';

/**
 * The shape of the ramp, as alpha against distance travelled.
 *
 * A two-stop gradient climbs at a constant rate from the instant it starts, and that corner
 * in the *rate* is visible even though the values either side of it are continuous — the eye
 * picks up the second derivative and reads a line across the top of the wash. Blurring the
 * element does not soften it: filters are applied before masks, so the mask's own edge stays
 * perfectly crisp however much blur the fill carries.
 *
 * So the ramp is eased rather than linear. These are smoothstep samples — 3t² − 2t³ — which
 * leaves at zero and arrives at one with the rate at nearly nothing on both ends. Sampled
 * densely enough that the straight segments CSS draws between them stay under the
 * threshold that banding becomes visible at.
 */
export const SCRIM_RAMP_EASING: ReadonlyArray<readonly [travelled: number, alpha: number]> = [
  [0, 0],
  [0.125, 0.051],
  [0.25, 0.156],
  [0.375, 0.316],
  [0.5, 0.5],
  [0.625, 0.684],
  [0.75, 0.844],
  [0.875, 0.949],
  [1, 1],
];

export const buildCopyScrim = (stageColor: string): string => {
  const stops = SCRIM_STOPS.map(([percent, alpha]) => `${withAlpha(stageColor, alpha)} ${percent}%`);

  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

/**
 * The mask that decides how far up the scrim reaches.
 *
 * Where the ramp *ends* is the part under review pressure: it has to outlast `copyTop` and
 * give out before the caps, or it paints the band of flat purple over the headline that
 * review kept returning to. So that landing point is held against the copy block and
 * everything else is worked around it — the box it is expressed against is taller than the
 * block at both ends now, by the overhang above and the bleed below, and both are taken
 * back out before the fraction is applied.
 *
 * Raising the top therefore lengthens the approach rather than moving the arrival: the
 * gradient starts `SCRIM_OVERHANG` higher and still reaches full strength exactly
 * `SCRIM_FADE_IN` into the copy.
 *
 * Only the top is shaped. The bottom stays fully opaque so the blurred fill runs past the
 * stage edge and gets clipped there rather than fading out inside the frame.
 */
export const buildCopyScrimMask = (bleed: string): string => {
  const fadeIn = Number.parseFloat(SCRIM_FADE_IN) / 100;
  const overhang = Number.parseFloat(SCRIM_OVERHANG) / 100;

  // `100% - bleed` is the copy block plus its overhang; dividing the pair back out leaves
  // the ramp measured in the block's own terms, which is where it is specified.
  const rampEnd = (overhang + fadeIn) / (1 + overhang);

  const stops = SCRIM_RAMP_EASING.map(
    ([travelled, alpha]) => `rgba(0, 0, 0, ${alpha}) calc((100% - ${bleed}) * ${rampEnd * travelled})`,
  );

  // Held from the ramp's end to the foot, so the only shaping is the approach.
  return `linear-gradient(to bottom, ${stops.join(', ')}, #000 100%)`;
};

/**
 * The card behind the code. Off-white rather than pure white: it sits on a purple stage,
 * and paper-white punches a hole in it.
 */
export const QR_CARD_COLOR = '#F5F0FF';

/**
 * A blur radius in canvas pixels, as a share of the stage.
 *
 * Left in fixed pixels it would drift sharper as the window grew — everything around it
 * scales — until the campaign was legible enough to fight the copy in front of it.
 */
export const desktopBlur = desktopPx;
